import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import BookingDetailCard from '@/components/BookingDetailCard';
import { DETAIL_SHOWCASE_MANIFEST } from '@/data/detailShowcase';
import { aud } from '@/utils/format';
import clsx from 'clsx';

/**
 * Dev-only walkthrough of every BookingDetailCard permutation seeded by
 * seedDetailShowcase (Prompt 6). Grouped by category with an Open button per row
 * so each distinct state can be inspected without hunting the calendar.
 */
export default function DetailShowcase() {
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const deposits = useBrainStore((s) => s.deposits);
  const initialised = useBrainStore((s) => s.initialised);
  const loadFixtures = useBrainStore((s) => s.loadFixtures);
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (!initialised) loadFixtures();
  }, [initialised, loadFixtures]);

  const grouped = useMemo(() => {
    const g: Array<{ category: string; rows: typeof DETAIL_SHOWCASE_MANIFEST }> = [];
    for (const e of DETAIL_SHOWCASE_MANIFEST) {
      let bucket = g.find((x) => x.category === e.category);
      if (!bucket) {
        bucket = { category: e.category, rows: [] };
        g.push(bucket);
      }
      bucket.rows.push(e);
    }
    return g;
  }, []);

  const openBooking = openId ? bookings[openId] : undefined;

  return (
    <section className="px-8 py-8" data-testid="detail-showcase">
      <header className="max-w-5xl">
        <p className="label-small-caps text-gold-bright">Dev tooling</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Booking detail showcase</h1>
        <p className="font-serif text-cream-soft mt-3 max-w-2xl">
          Every permutation the booking detail card renders — {DETAIL_SHOWCASE_MANIFEST.length} seeded
          bookings across {grouped.length} categories. Open any row to inspect its state.
        </p>
      </header>

      <div className="mt-8 space-y-8 max-w-5xl">
        {grouped.map((group) => (
          <div key={group.category} data-testid={`showcase-group-${group.category}`}>
            <p className="label-small-caps text-cream-soft mb-2">
              {group.category} · {group.rows.length}
            </p>
            <div className="overflow-x-auto rounded-md border border-gold-dim/30">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-cream-faint">
                    <th className="px-3 py-2 font-normal">Permutation</th>
                    <th className="px-3 py-2 font-normal">Status</th>
                    <th className="px-3 py-2 font-normal">Deposit</th>
                    <th className="px-3 py-2 font-normal">Invoice</th>
                    <th className="px-3 py-2 font-normal text-right">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => {
                    const b = bookings[row.id];
                    const dep = b?.depositId ? deposits[b.depositId] : undefined;
                    const depLabel = dep
                      ? `${dep.status} · ${aud(dep.amountPaid ?? (dep.status === 'paid' ? dep.amountAud : 0))}/${aud(dep.amountAud)}`
                      : '—';
                    return (
                      <tr
                        key={row.id}
                        className="border-t border-gold-dim/20 text-cream-soft hover:bg-ink-soft/40 transition"
                      >
                        <td className="px-3 py-2 text-cream">{row.label}</td>
                        <td className="px-3 py-2 capitalize">{b ? b.status.replace('-', ' ') : '—'}</td>
                        <td className="px-3 py-2">{depLabel}</td>
                        <td className="px-3 py-2">{b?.invoiceStatus ?? '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            disabled={!b}
                            onClick={() => setOpenId(row.id)}
                            className={clsx(
                              'btn-ghost text-xs',
                              !b && 'opacity-40 cursor-not-allowed',
                            )}
                            data-testid={`showcase-open-${row.id}`}
                          >
                            {b ? 'Open' : 'Missing'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {openBooking ? (
        <BookingDetailCard
          booking={openBooking}
          customer={customers[openBooking.customerId]}
          artist={artists[openBooking.artistId]}
          deposit={openBooking.depositId ? deposits[openBooking.depositId] : undefined}
          onClose={() => setOpenId(null)}
          onNavigateBooking={(id) => setOpenId(id)}
          onViewCustomer={() => {
            const cId = openBooking.customerId;
            setOpenId(null);
            navigate(`/customer/${cId}`);
          }}
        />
      ) : null}
    </section>
  );
}
